import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Typography, List, ListItem, ListItemText, Box, Button, TextField, MenuItem, Stack, Switch, Chip, InputAdornment } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import missionValuesService, { MissionValue } from '../../services/missionValuesService';
import { usePageTitle } from '../../hooks/usePageTitle';

const MissionValuePage: React.FC = () => {
  usePageTitle('Mission Value');
  
  const navigate = useNavigate();
  const [items, setItems] = useState<MissionValue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<Partial<MissionValue>>({});
  const [q, setQ] = useState<string>('');
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const load = async (query?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await missionValuesService.list(query);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load mission values');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (editingId && firstFieldRef.current) firstFieldRef.current.focus(); }, [editingId]);

  const startEdit = (it: MissionValue) => { setEditingId(it._id); setForm({ ...it }); };
  const startCreate = () => { setEditingId('new'); setForm({ name: '', valueType: 'Other', priority: 'Medium', order: (items?.length || 0) + 1, isActive: true }); };
  const cancelEdit = () => { setEditingId(null); setForm({}); };

  const save = async () => {
    try {
      if (editingId === 'new') {
        const created = await missionValuesService.create({
          name: form.name || '',
          valueType: (form.valueType as any) || 'Other',
          description: form.description, stakeholder: form.stakeholder,
          priority: form.priority || 'Medium',
          order: form.order, isActive: form.isActive,
        });
        setItems((prev) => [created, ...prev]);
      } else if (editingId) {
        const updated = await missionValuesService.update(editingId, form);
        setItems((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      }
      cancelEdit();
    } catch (e: any) { setError(e?.response?.data?.error || 'Save failed'); }
  };

  const remove = async (id: string) => {
    try {
      await missionValuesService.remove(id);
      setItems((prev) => prev.filter((p) => p._id !== id));
      if (editingId === id) cancelEdit();
    } catch (e: any) { setError(e?.response?.data?.error || 'Delete failed'); }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(items)) return [];
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((i) => [i.name, i.valueType, i.description, i.stakeholder].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [items, q]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  const renderForm = (isNew: boolean) => (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <TextField inputRef={firstFieldRef} label="Name" fullWidth value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      <Stack direction="row" spacing={1}>
        <TextField select label="Value Type" sx={{ width: 180 }} value={form.valueType || 'Other'} onChange={(e) => setForm((f) => ({ ...f, valueType: e.target.value as any }))}>
          {['Mission', 'Objective', 'Goal', 'Metric', 'Outcome', 'Other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField select label="Priority" sx={{ width: 150 }} value={form.priority || 'Medium'} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as any }))}>
          {['Critical', 'High', 'Medium', 'Low'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        <TextField label="Stakeholder" sx={{ flex: 1 }} value={form.stakeholder || ''} onChange={(e) => setForm((f) => ({ ...f, stakeholder: e.target.value }))} />
        <TextField label="Order" type="number" sx={{ width: 100 }} value={form.order ?? ''} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography component="span">Active</Typography>
          <Switch checked={!!form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
        </Stack>
      </Stack>
      <TextField label="Description" multiline minRows={2} value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
      <Stack direction="row" spacing={1}>
        <Button onClick={save} startIcon={<SaveIcon />} variant="contained">Save</Button>
        <Button onClick={cancelEdit} startIcon={<CloseIcon />} color="inherit">Cancel</Button>
        {!isNew && <Button onClick={() => remove(form._id!)} startIcon={<DeleteIcon />} color="error">Delete</Button>}
      </Stack>
    </Stack>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button onClick={() => navigate('/dhs')} startIcon={<ArrowBackIcon />} sx={{ mr: 2 }}>Back</Button>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search mission values" value={q} onChange={(e) => setQ(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <Button onClick={startCreate} startIcon={<AddIcon />} sx={{ ml: 2 }}>Add</Button>
      </Box>
      <Typography variant="h4" component="h1" sx={{ color: '#003366', fontWeight: 700, mb: 2 }}>Mission Value</Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>Strategic missions, objectives, goals, metrics, and outcomes that drive DHS operations.</Typography>
      {error && <Typography role="alert" sx={{ color: 'error.main', mb: 2 }}>{error}</Typography>}
      <List>
        {editingId === 'new' && <ListItem divider sx={{ bgcolor: 'background.default' }}>{renderForm(true)}</ListItem>}
        {filtered.map((it) => (
          <ListItem key={it._id} divider>
            {editingId === it._id ? renderForm(false) : (
              <Box role="button" tabIndex={0} onClick={() => startEdit(it)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(it); }}} style={{ width: '100%', outline: 'none' }}>
                <Stack spacing={0.5}>
                  <ListItemText primaryTypographyProps={{ sx: { fontWeight: 700 } }} primary={it.name} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" color="primary" label={it.valueType} />
                    <Chip size="small" label={it.priority} color={getPriorityColor(it.priority)} />
                    {it.stakeholder && <Chip size="small" label={it.stakeholder} />}
                    <Chip size="small" label={it.isActive ? 'Active' : 'Inactive'} color={it.isActive ? 'success' : 'default'} />
                  </Stack>
                  {it.description && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{it.description}</Typography>}
                </Stack>
              </Box>
            )}
          </ListItem>
        ))}
        {loading && <ListItem><ListItemText primary="Loading..." /></ListItem>}
        {!loading && filtered.length === 0 && <ListItem><ListItemText primary="No mission values found" /></ListItem>}
      </List>
    </Container>
  );
};

export default MissionValuePage;
